/** @format */

import 'src/i18n';
import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import { resolveSelectedLanguage, getContentLanguage } from 'src/utils/locale';
import { resolveExtranetApiLanguageCode, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import type { SearchApiResponse, TopicsPageResponse } from 'src/types';

export const getDefaultSearchLanguage = () => resolveExtranetSelectableLocale(resolveSelectedLanguage());

const initialState = {
  selectedBrand: 'all',
  selectedTopicId: null,
  selectedLanguage: getDefaultSearchLanguage(),
  selectedSortBy: 'latest',
  selectedKeywords: [],
  topicOptionsLoading: false,
  topicOptions: [],
  topicOptionsError: null,
  resultsLoading: false,
  resultsAppending: false,
  results: [],
  resultsNextCursor: null,
  resultsTotal: 0,
  resultsError: null
};

const slice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSelectedBrand(state, action) {
      state.selectedBrand = action.payload || 'all';
    },
    setSelectedTopicId(state, action) {
      state.selectedTopicId = action.payload || null;
    },
    setSelectedLanguage(state, action) {
      state.selectedLanguage = resolveExtranetSelectableLocale(action.payload, state.selectedLanguage || 'en');
    },
    setSelectedSortBy(state, action) {
      state.selectedSortBy = action.payload || 'latest';
    },
    setSelectedKeywords(state, action) {
      state.selectedKeywords = Array.isArray(action.payload) ? action.payload : [];
    },
    getTopicOptions(state) {
      state.topicOptionsLoading = true;
      state.topicOptionsError = null;
    },
    getTopicOptionsSuccess(state, action) {
      state.topicOptionsLoading = false;
      state.topicOptions = Array.isArray(action.payload) ? action.payload : [];
    },
    getTopicOptionsFailure(state, action) {
      state.topicOptionsLoading = false;
      state.topicOptionsError = action.payload;
    },
    getSearchResults(state, action) {
      state.resultsLoading = true;
      state.resultsAppending = !!action?.payload?.append;
      state.resultsError = null;
    },
    getSearchResultsSuccess(state, action) {
      const { items, nextCursor, total, append } = action.payload || {};
      const safeItems = Array.isArray(items) ? items : [];
      const currentItems = Array.isArray(state.results) ? state.results : [];
      state.results = append ? [...currentItems, ...safeItems] : safeItems;
      state.resultsNextCursor = nextCursor ?? null;
      state.resultsTotal = typeof total === 'number' ? total : state.resultsTotal;
      state.resultsLoading = false;
      state.resultsAppending = false;
    },
    getSearchResultsFailure(state, action) {
      state.resultsLoading = false;
      state.resultsAppending = false;
      state.resultsError = action.payload;
    }
  }
});

export const reducer = slice.reducer;

function parseSearchPayload(data) {
  if (Array.isArray(data)) {
    return { items: data, nextCursor: null, total: data.length };
  }
  return {
    items: Array.isArray(data?.searchResults) ? data.searchResults : [],
    nextCursor: data?.nextCursor ?? null,
    total: typeof data?.total === 'number' ? data.total : 0
  };
}

function normalizeContentItem(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const rawLanguages = item.languages ?? item.language ?? item.locale ?? '';
  const normalizedLanguages = Array.isArray(rawLanguages)
    ? rawLanguages
    : typeof rawLanguages === 'string' && rawLanguages.trim()
      ? rawLanguages
          .split('|')
          .map(language => language.trim())
          .filter(Boolean)
      : [];

  const normalizedText = item.text || item.teaser || item.description || '';

  return {
    ...item,
    date: item.creationDate || item.date || '',
    text: normalizedText,
    description: normalizedText,
    tags: Array.isArray(item.tags) ? item.tags : [],
    languages: normalizedLanguages
  };
}

function resolveSearchOrder(selectedSortBy) {
  if (!selectedSortBy) {
    return 'latest';
  }
  const normalized = String(selectedSortBy).trim().toLowerCase();
  const supportedOrders = ['latest', 'relevant'];
  return supportedOrders.includes(normalized) ? normalized : 'latest';
}

function parseTopicsPagePayload(data) {
  if (Array.isArray(data)) {
    return { topics: data, nextCursor: null };
  }
  return {
    topics: Array.isArray(data?.topics) ? data.topics : [],
    nextCursor: data?.nextCursor ?? null
  };
}

async function fetchAllTopicsPages({ language }: { language?: string | null } = {}) {
  let mergedTopics = [];
  let nextCursor = null;

  do {
    const params = new URLSearchParams();
    if (nextCursor) {
      params.set('cursor', nextCursor);
    }
    const qs = params.toString();
    const url = `${ENDPOINTS.GET_TOPICS_ENDPOINT}${qs ? `?${qs}` : ''}`;
    const response = await axios.get<TopicsPageResponse>(url, {
      ...(language
        ? { acceptLanguage: getContentLanguage(resolveExtranetApiLanguageCode(language) || language) }
        : {})
    });
    const { topics, nextCursor: cursor } = parseTopicsPagePayload(response?.data);
    mergedTopics = [...mergedTopics, ...topics];
    nextCursor = cursor || null;
  } while (nextCursor);

  return mergedTopics;
}

function resolveTopicIdForApi(topicId) {
  if (topicId === undefined || topicId === null || topicId === '') {
    return null;
  }
  const parsed = Number(topicId);
  return Number.isFinite(parsed) ? parsed : null;
}

export const getTopicOptions =
  (language) =>
  async (dispatch, getState) => {
    const { getTopicOptions, getTopicOptionsSuccess, getTopicOptionsFailure } = slice.actions;
    const resolvedLanguage = language ?? getState().search.selectedLanguage;

    try {
      dispatch(getTopicOptions());
      const topics = await fetchAllTopicsPages({ language: resolvedLanguage });
      const seenLabels = new Set();
      const level1Topics = (Array.isArray(topics) ? topics : [])
        .filter(topic => topic && typeof topic === 'object' && topic.id != null && typeof topic.name === 'string')
        .map(topic => ({
          key: topic.id,
          label: topic.name.trim()
        }))
        .filter(topic => {
          const idStr = topic.key !== undefined && topic.key !== null ? String(topic.key).trim() : '';
          if (!idStr || !topic.label) return false;
          const normalizedLabel = topic.label.toLowerCase();
          if (seenLabels.has(normalizedLabel)) return false;
          seenLabels.add(normalizedLabel);
          return true;
        });
      dispatch(getTopicOptionsSuccess(level1Topics));
    } catch (error) {
      dispatch(getTopicOptionsFailure(error?.message || 'Failed to load topic options'));
    }
  };

export const getSearchResults =
  ({ cursor = null, append = false, limit = 25 } = {}) =>
  async (dispatch, getState) => {
    const { getSearchResults, getSearchResultsSuccess, getSearchResultsFailure } = slice.actions;
    try {
      dispatch(getSearchResults({ append }));
      const { search } = getState();
      const order = resolveSearchOrder(search?.selectedSortBy);
      const keywords = Array.isArray(search?.selectedKeywords) ? search.selectedKeywords.filter(Boolean) : [];
      const resolvedTopicId = resolveTopicIdForApi(search?.selectedTopicId);
      const topicIds = resolvedTopicId != null ? [resolvedTopicId] : [];
      const searchParams = {
        ...(search?.selectedBrand && search.selectedBrand !== 'all' ? { brand: search.selectedBrand } : {}),
        ...(search?.selectedLanguage
          ? {
              'languages[]': resolveExtranetApiLanguageCode(search.selectedLanguage) || search.selectedLanguage
            }
          : {}),
        ...(order ? { order } : {}),
        ...(topicIds.length > 0 ? { 'topicIds[]': topicIds } : {}),
        ...(keywords.length > 0 ? { 'keywords[]': keywords } : {}),
        ...(Number.isFinite(limit) ? { limit } : {}),
        ...(cursor ? { cursor } : {})
      };
      const response = await axios.get<SearchApiResponse>(ENDPOINTS.SEARCH_ENDPOINT, { params: searchParams });
      const { items, nextCursor, total } = parseSearchPayload(response?.data);
      const nextItems = items.map(normalizeContentItem).filter(Boolean);
      dispatch(getSearchResultsSuccess({ items: nextItems, nextCursor, total, append }));
    } catch (error) {
      dispatch(getSearchResultsFailure(error?.message || 'Failed to load search results'));
    }
  };

export const { setSelectedBrand, setSelectedTopicId, setSelectedLanguage, setSelectedSortBy, setSelectedKeywords } =
  slice.actions;
